import type { ExitsInvestmentsToCompleteProfile } from '@shared/schemas';
import { all, call, cancelled, getContext, put, takeLatest } from 'redux-saga/effects';

import { logger } from '@/utils/logger';

import type { ExitsInvestmentsReviewGateway } from '../infrastructure/exits-investments-review.gateway';
import { loadRequested } from './exits-investments-review.actions';
import { exitsInvestmentsReviewActions } from './exits-investments-review.slice';

/**
 * WORKER: the queue (drives the page's tile, table, error and Retry). Read
 * only here, when the page opens or on Retry (#762): it is the slowest list of
 * the admin (some 7 s in production), and no other page waits on it any more.
 *
 * Cancelled by `takeLatest` when the page is left (`pageLeft`) or a new read
 * replaces this one (Retry): the HTTP request is then aborted too, as the
 * file upload of `add-list` aborts its own. An abort is never a failure: no
 * `loadFailed`, no warning; the state stays as `pageLeft` or the new read set it.
 */
function* loadWorker(action: { type: string }) {
  // Leaving the page cancels a read still running (`takeLatest`), and starts none.
  if (exitsInvestmentsReviewActions.pageLeft.match(action)) return;
  const gateway: ExitsInvestmentsReviewGateway = yield getContext('exitsInvestmentsReviewGateway');
  const controller = new AbortController();
  yield put(exitsInvestmentsReviewActions.loadStarted());
  try {
    const profiles: ExitsInvestmentsToCompleteProfile[] = yield call(
      gateway.loadExitsInvestmentsToCompleteProfiles,
      controller.signal,
    );
    yield put(exitsInvestmentsReviewActions.loadSucceeded(profiles));
  } catch (e) {
    logger.warn('[exits-investments-review] The queue could not be read', e);
    yield put(exitsInvestmentsReviewActions.loadFailed());
  } finally {
    // The browser drops the request; the server still finishes its count.
    if ((yield cancelled()) as boolean) controller.abort();
  }
}

/** WATCHERS. */
export function* exitsInvestmentsReviewSaga() {
  yield all([
    takeLatest([loadRequested.type, exitsInvestmentsReviewActions.pageLeft.type], loadWorker),
  ]);
}
